import React, { useEffect, useState, useMemo, useCallback } from "react";
import Styles from "./DirectDocumentation.module.css";
import { Modal } from "@mui/material";
import { Close } from "@mui/icons-material";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

// Icons
import { ReactComponent as ViewIcon } from "../../../../assets/images/view.svg";
import { ReactComponent as UploadIcon } from "../../../../assets/images/uploadIcon.svg";
import { ReactComponent as ViewUploadFile } from "../../../../assets/images/viewUploadFile.svg";
import { ReactComponent as CrossIcon } from "../../../../assets/images/circlex.svg";
import { ReactComponent as DownloadIcon } from "../../../../assets/images/downloadFile.svg";

// Capacity APIs
import {
  generateLoiDocument as generateLoiCapacity,
  generateLoaDocument as generateLoaCapacity,
  generateFinalDocument as generateFaCapacity,
} from "../../../../Services/ApiCalling/Listing";

import {
  approveDocuments,
  rejectAgreement,
  submitDocuments,
  uploadLoiFile as uploadLoiCapacity,
  uploadLoaFile as uploadLoaCapacity,
  uploadFaFile as uploadFaCapacity,
  uploadOtherFile as uploadOtherCapacity,
} from "../../../../Services/BuySell/Listing";

// Banking APIs
import {
  generateLoiDocument as generateLoiBanking,
  generateLoaDocument as generateLoaBanking,
  generateFaDocument as generateFaBanking,
  uploadLoiFile as uploadLoiBanking,
  uploadLoaFile as uploadLoaBanking,
  uploadFaFile as uploadFaBanking,
  uploadLedgerFile,
  acceptRejectAgreement,
  getDocumentUrlByLedgerId,
} from "../../../../Services/ApiCalling/banking";

// Redux Actions - Capacity
import {
  fetchActiveConnection,
  fetchActiveLedger as fetchActiveLedgerCapacity,
  fetchMainScreenActiveConnection,
  fetchViewDocumentHistory as fetchViewDocumentHistoryCapacity,
} from "../../../../Redux/Slices/CapacitySlice";

// Redux Actions - Banking
import {
  fetchActiveLedger as fetchActiveLedgerBanking,
  fetchViewDocumentHistory as fetchViewDocumentHistoryBanking,
  fetchMatchmakingResults,
  fetchAllBankingListings,
} from "../../../../Redux/Slices/bankingSlice";

// Components
import PreviewModal from "../../../../commons/PreviewModal/PreviewModal";
import ConfirmationModal from "../../../../commons/ConfirmationModal/ConfirmationModal";
import Ongoing from "../../../../Components/Modals/ConGen/Ongoing/Ongoing";
import SuccessPop from "../../../../commons/PopupModal/BankingPopup/SuccessPop/SuccessPop";

import { formatDate } from "../../../../helpers/dateFormat";
import { toastProperties } from "../../../../commons/Toast/Toaster";

const DirectDocumentation = ({
  open,
  handleClose,
  connectionId,
  onAllDocsUploaded,
  agreementStatus,
  activeCounterparty,
  onRefresh,
  mode = "capacity",

  // Capacity-specific props
  data,
  capacityId,
  type,
  viewType,

  // Banking-specific props
  isCounterparty,
  upData,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();

  const capacityState = useSelector((state) => state.CapacitySlice);
  const bankingState = useSelector((state) => state.bankingSlice);

  const id = useSelector((state) => state.CapacitySlice?.activeMain?.id);
  const role = useSelector(
    (state) => state?.persistedReducer?.user?.activeRole,
  )?.toLowerCase();

  const mainScreenDataCapacity = useSelector(
    (state) => state.CapacitySlice?.mainScreenActiveConnection,
  );

  const listingId =
    mode === "capacity"
      ? capacityId || location?.search?.split("=")[1] || id
      : upData?.[0]?.selfListing?.id;

  const mainScreenData = mode === "capacity" ? mainScreenDataCapacity : null;

  const { documentViewHistory, loading, error } =
    mode === "capacity" ? capacityState : bankingState;

  const activeConnection = useMemo(() => {
    if (mode !== "capacity" || !connectionId || !mainScreenData) return null;

    if (Array.isArray(mainScreenData)) {
      return (
        mainScreenData.find(
          (item) => Number(item?.id) === Number(connectionId),
        ) || null
      );
    }

    if (
      typeof mainScreenData === "object" &&
      Number(mainScreenData?.id) === Number(connectionId)
    ) {
      return mainScreenData;
    }

    return null;
  }, [mode, mainScreenData, connectionId]);

  // State management
  const [selectedFiles, setSelectedFiles] = useState({
    loi: null,
    loa: null,
    fa: null,
    other: null,
  });

  const [uploadedDocumentIds, setUploadedDocumentIds] = useState({
    loi: null,
    loa: null,
    fa: null,
    other: null,
  });

  const [comments, setComments] = useState("");
  const [uploading, setUploading] = useState(false);
  const [approvalLoading, setApprovalLoading] = useState(false);

  // Modal states
  const [previewModal, setPreviewModal] = useState(false);
  const [previewFileUrl, setPreviewFileUrl] = useState(null);
  const [uploadConfirmOpen, setUploadConfirmOpen] = useState(false);
  const [pendingUploadData, setPendingUploadData] = useState(null);
  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [rejectionModalOpen, setRejectionModalOpen] = useState(false);
  const [conformOpen, setConformOpen] = useState(false);
  const [conformReject, setConformReject] = useState(false);
  const [ongoing, setOngoing] = useState(false);
  const [hasJustUploaded, setHasJustUploaded] = useState(false);

  const [successPopupOpen, setSuccessPopupOpen] = useState(false);

  // Banking-specific states
  const [hasNewLoiOrLoaUpload, setHasNewLoiOrLoaUpload] = useState(false);
  const [dataFetched, setDataFetched] = useState(false);
  const [latestFormData, setLatestFormData] = useState(null);
  const [uploadSuccessful, setUploadSuccessful] = useState(false);

  // API Configuration based on mode
  const apiConfig = useMemo(() => {
    if (mode === "capacity") {
      return {
        generateLoi: generateLoiCapacity,
        generateLoa: generateLoaCapacity,
        generateFa: generateFaCapacity,
        uploadLoi: uploadLoiCapacity,
        uploadLoa: uploadLoaCapacity,
        uploadFa: uploadFaCapacity,
        uploadOther: uploadOtherCapacity,
        fetchDocumentHistory: fetchViewDocumentHistoryCapacity,
        fetchActiveLedger: fetchActiveLedgerCapacity,
      };
    } else {
      return {
        generateLoi: generateLoiBanking,
        generateLoa: generateLoaBanking,
        generateFa: generateFaBanking,
        uploadLoi: uploadLoiBanking,
        uploadLoa: uploadLoaBanking,
        uploadFa: uploadFaBanking,
        uploadOther: uploadLedgerFile,
        fetchDocumentHistory: fetchViewDocumentHistoryBanking,
        fetchActiveLedger: fetchActiveLedgerBanking,
      };
    }
  }, [mode]);

  // Computed values
  const showApprovalButtons =
    mode === "capacity"
      ? agreementStatus
      : agreementStatus?.toUpperCase() === "UPLOADED" && isCounterparty;

  const shouldEnableUploads =
    mode === "capacity"
      ? !agreementStatus && type !== "view"
      : agreementStatus?.toUpperCase() === "NOT_UPLOADED" ||
        showApprovalButtons;

  const isFirstParty = mode === "capacity" ? !agreementStatus : !isCounterparty;

  const finalAgreementUploadedOrEditing =
    isFirstParty &&
    (selectedFiles.fa ||
      activeCounterparty?.agreementDocumentHistory?.length > 0);

  const shouldDisableUploads = showApprovalButtons;

  // Document state checks
  const documentStates = {
    isLoiDisabled:
      shouldDisableUploads ||
      (agreementStatus && !shouldEnableUploads) ||
      type === "view",

    isLoaDisabled:
      shouldDisableUploads ||
      (agreementStatus && !shouldEnableUploads) ||
      type === "view",

    isOtherDocumentsDisabled:
      shouldDisableUploads ||
      (agreementStatus && !shouldEnableUploads) ||
      type === "view",

    isFinalAgreementDisabled:
      shouldDisableUploads ||
      (agreementStatus && !shouldEnableUploads) ||
      (agreementStatus && finalAgreementUploadedOrEditing) ||
      type === "view",
  };

  useEffect(() => {
    // Reset hasJustUploaded when modal closes or when rejection happens
    if (!open || (showApprovalButtons && rejectionModalOpen === false)) {
      setHasJustUploaded(false);
    }
  }, [open, showApprovalButtons, rejectionModalOpen]);

  // Helper functions
  const updateSelectedFile = (type, file) => {
    setSelectedFiles((prev) => ({ ...prev, [type]: file }));
  };

  const updateUploadedDocId = (type, id) => {
    setUploadedDocumentIds((prev) => ({ ...prev, [type]: id }));
  };

  const resetFileSelection = (type) => {
    updateSelectedFile(type, null);
    updateUploadedDocId(type, null);
    if (type === "other") {
      setComments("");
    }
  };

  const handleCancelUpload = () => {
    if (pendingUploadData?.type) {
      resetFileSelection(pendingUploadData.type);
    }
    setPendingUploadData(null);
    setUploadConfirmOpen(false);
  };

  // Document generation handlers
  const documentGenerators = {
    loi: async () => {
      if (!connectionId) return;
      try {
        const response = await apiConfig.generateLoi(connectionId);
        const blob = response.data;
        const url = window.URL.createObjectURL(
          new Blob([blob], {
            type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          }),
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "Letter_of_Intent.docx");
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success("LOI DOCX generated and downloaded successfully!");
      } catch (error) {
        toast.error(
          error?.response?.data?.message || "Failed to generate LOI DOCX.",
        );
      }
    },

    loa: async () => {
      if (!connectionId) return;
      try {
        const response = await apiConfig.generateLoa(connectionId);
        const blob = response.data;
        const url = window.URL.createObjectURL(
          new Blob([blob], {
            type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          }),
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "Letter_of_Award.docx");
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success("LOA DOCX generated and downloaded successfully!");
      } catch (error) {
        toast.error(
          error?.response?.data?.message || "Failed to generate LOA DOCX.",
        );
      }
    },

    fa: async () => {
      if (!connectionId) return;
      try {
        const response = await apiConfig.generateFa(connectionId);
        const blob = response.data;
        const url = window.URL.createObjectURL(
          new Blob([blob], {
            type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          }),
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "Final_Agreement.docx");
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success(
          "Final Agreement DOCX generated and downloaded successfully!",
        );
      } catch (error) {
        toast.error(
          error?.response?.data?.message ||
            "Failed to generate Final Agreement DOCX.",
        );
      }
    },
  };

  // Upload handlers
  const performUpload = async () => {
    if (!pendingUploadData) return;
    const { type, displayName } = pendingUploadData;

    if (!connectionId) {
      toast.error("Connection ID is missing.");
      handleCancelUpload();
      return;
    }

    if (type === "other" && !selectedFiles.other) {
      toast.error("Please select a document to upload!");
      handleCancelUpload();
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append("file", selectedFiles[type]);

      if (type === "other") {
        await apiConfig.uploadOther(
          formData,
          connectionId,
          listingId,
          comments.trim(),
        );
        toast.success("Other document uploaded successfully!");
        setHasJustUploaded(true);
        updateSelectedFile("other", null);
        setComments("");
        dispatch(apiConfig.fetchDocumentHistory(connectionId));
      } else {
        const uploadFn =
          type === "loi"
            ? apiConfig.uploadLoi
            : type === "loa"
              ? apiConfig.uploadLoa
              : apiConfig.uploadFa;

        const response = await uploadFn(formData, connectionId, listingId);

        if (response?.data?.id) {
          updateUploadedDocId(type, response.data.id);
        }

        if (type === "loi" || type === "loa") {
          setLatestFormData(formData);
        }

        toast.success(`${displayName} uploaded successfully!`);
        setHasJustUploaded(true);

        if (type === "loi" || type === "loa" || type === "fa") {
          setHasNewLoiOrLoaUpload(true);
        }

        updateSelectedFile(type, null);

        // Handle all document types with consistent behavior
        if (type === "loi" || type === "loa" || type === "fa") {
          if (mode === "capacity") {
            if (
              (role === "generator" &&
                activeConnection?.conAgreementAcceptanceStatus) ||
              (role === "consumer" &&
                activeConnection?.genAgreementAcceptanceStatus)
            ) {
              setPendingUploadData(null);
              setUploadConfirmOpen(false);
              return;
            }
            // Set flag to trigger the effect - don't call handlers directly
            setUploadSuccessful(true);
          } else {
            // Banking mode - fetch history without closing modal
            dispatch(apiConfig.fetchDocumentHistory(connectionId));
            if(!showApprovalButtons && type === "fa"){
              handleClose();
            }
          }
        }
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          `Failed to upload ${displayName || "document"}. Please try again.`,
      );
    } finally {
      setUploading(false);
      setPendingUploadData(null);
      setUploadConfirmOpen(false);
    }
  };
  // File change handler
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      toast.error("Only PDF files are allowed!");
      e.target.value = null;
      return;
    }

    const inputName = e.target.name;
    const documentTypeMap = {
      fileUpload: { type: "loi", displayName: "Letter of Intent" },
      fileUploadLoa: { type: "loa", displayName: "Letter of Award" },
      fileUploadFa: { type: "fa", displayName: "Final Agreement" },
      fileUploadOther: { type: "other", displayName: "Other Documents" },
    };

    const docInfo = documentTypeMap[inputName];
    if (!docInfo) return;

    updateSelectedFile(docInfo.type, file);

    // Set the flag to indicate a document has been uploaded
    setHasJustUploaded(true);

    if (docInfo.type === "other") {
      setPendingUploadData({
        type: "other",
        displayName: "Other Documents",
      });
      setUploadConfirmOpen(true);
      e.target.value = null;
      return;
    }

    setPendingUploadData({
      type: docInfo.type,
      displayName: docInfo.displayName,
    });
    setUploadConfirmOpen(true);
    e.target.value = null;
  };

  // View document handlers
  const handleViewDoc = async (id) => {
    try {
      const response = await getDocumentUrlByLedgerId(id);
      const url = response.data;
      if (url) {
        setPreviewFileUrl(url);
        setPreviewModal(true);
      } else {
        toast.error("Document URL not found");
      }
    } catch (err) {
      toast.error("Could not fetch document URL.");
    }
  };

  const documentViewers = {
    loi: () => {
      if (uploadedDocumentIds.loi) {
        handleViewDoc(uploadedDocumentIds.loi);
      } else {
        const loiDocs = documentViewHistory
          ?.filter(
            (doc) =>
              doc.filename?.toLowerCase().includes("loi") ||
              doc.filename?.toLowerCase().includes("letter_of_intent") ||
              doc.documentType === "LOI",
          )
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        const loiDoc = loiDocs?.[0];
        if (loiDoc) {
          handleViewDoc(loiDoc.id);
        } else {
          toast.info("No LOI document has been uploaded yet.");
        }
      }
    },

    loa: () => {
      if (uploadedDocumentIds.loa) {
        handleViewDoc(uploadedDocumentIds.loa);
      } else {
        const loaDocs = documentViewHistory
          ?.filter(
            (doc) =>
              doc.filename?.toLowerCase().includes("loa") ||
              doc.filename?.toLowerCase().includes("letter_of_agreement") ||
              doc.documentType === "LOA",
          )
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        const loaDoc = loaDocs?.[0];
        if (loaDoc) {
          handleViewDoc(loaDoc.id);
        } else {
          toast.info("No LOA document has been uploaded yet.");
        }
      }
    },

    fa: () => {
      if (uploadedDocumentIds.fa) {
        handleViewDoc(uploadedDocumentIds.fa);
        return;
      }

      if (documentViewHistory && documentViewHistory.length > 0) {
        const faDocs = documentViewHistory
          ?.filter(
            (doc) =>
              doc.documentType === "FA" ||
              doc.documentType === "AGREEMENT" ||
              doc.filename?.toLowerCase().includes("final") ||
              doc.filename?.toLowerCase().includes("agreement") ||
              doc.filename?.toLowerCase().includes("fa") ||
              (selectedFiles.fa && doc.filename === selectedFiles.fa.name),
          )
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        const faDoc = faDocs?.[0];
        if (faDoc) {
          handleViewDoc(faDoc.id);
        } else {
          if (selectedFiles.fa) {
            toast.error(
              "Could not find the uploaded Final Agreement document. Please try refreshing.",
            );
          } else {
            toast.info("No Final Agreement document has been uploaded yet.");
          }
        }
      } else {
        toast.info("No documents found in history.");
      }
    },

    other: () => {
      if (uploadedDocumentIds.other) {
        handleViewDoc(uploadedDocumentIds.other);
      } else {
        const otherDoc = documentViewHistory?.find(
          (doc) =>
            doc.documentType === "OTHER" ||
            doc.documentType === "RANDOM" ||
            (!doc.filename?.toLowerCase().includes("loi") &&
              !doc.filename?.toLowerCase().includes("loa") &&
              !doc.filename?.toLowerCase().includes("final_agreement") &&
              doc.documentType !== "AGREEMENT" &&
              doc.documentType !== "LOI" &&
              doc.documentType !== "LOA"),
        );
        if (otherDoc) {
          handleViewDoc(otherDoc.id);
        } else {
          toast.info("No other document has been uploaded yet.");
        }
      }
    },
  };

  // Download handler
  const handleDownloadDocument = async (docId, filename) => {
    try {
      const response = await getDocumentUrlByLedgerId(docId);
      const url = response.data;
      if (url) {
        const link = document.createElement("a");
        link.href = url;
        link.download = filename || "document.pdf";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success("Document downloaded successfully!");
      } else {
        toast.error("Could not fetch document URL");
      }
    } catch (err) {
      toast.error("Failed to download document. Please try again.");
    }
  };

  const handleApprove = async () => {
    if (mode === "capacity") {
      if (!hasCurrentLoiOrLoa) {
        toast.info("Please upload atleast one document before submitting.");
        return;
      }
      setApprovalLoading(true);
      try {
        const payload = {
          userType: role,
          consumerName: activeConnection?.capacityConsumer?.name,
          generatorName: activeConnection?.capacityGenerator?.name,
          id: activeConnection?.id,
        };
        const res = await approveDocuments(payload);
        if (res?.data?.code === 200) {
          if (
            location.pathname === "/dashboard" ||
            location.pathname.includes("/bilateral") ||
            location.pathname.includes("/tender_active")
          ) {
            dispatch(fetchActiveConnection({ curr: 0, capId: listingId }));
            dispatch(fetchMainScreenActiveConnection({ capId: listingId }));
            handleClose();
            return;
          }
          setConformOpen(false);
          if (viewType !== "RA_TERMS" && viewType !== "RA_ACTIVE") {
            setOngoing(true);
          }
          handleClose();
        }
      } catch {
        toast.error("Could not approve documents.", toastProperties);
      } finally {
        setApprovalLoading(false);
      }
    } else {
      // Banking mode approval - determine current user party
      const getCurrentUserParty = () => {
        if (!activeCounterparty) return null;
        const currentUserId = upData?.[0]?.selfListing?.userId;
        if (currentUserId === activeCounterparty.connectionInitiatorId) {
          return "INITIATOR";
        } else if (currentUserId === activeCounterparty.connectionAcceptorId) {
          return "ACCEPTOR";
        }
        return null;
      };

      const currentUserParty = getCurrentUserParty();
      let currentPartyDocIds;

      if (currentUserParty === "INITIATOR") {
        currentPartyDocIds = {
          loiDocId: activeCounterparty?.partyOneLoiDocumentId,
          loaDocId: activeCounterparty?.partyOneLoaDocumentId,
          agreementDocId: activeCounterparty?.partyOneAgreementDocumentId,
        };
      } else if (currentUserParty === "ACCEPTOR") {
        currentPartyDocIds = {
          loiDocId: activeCounterparty?.partyTwoLoiDocumentId,
          loaDocId: activeCounterparty?.partyTwoLoaDocumentId,
          agreementDocId: activeCounterparty?.partyTwoAgreementDocumentId,
        };
      }

      const hasAnyUploadedDoc = !!(
        currentPartyDocIds?.loiDocId ||
        currentPartyDocIds?.loaDocId ||
        currentPartyDocIds?.agreementDocId
      );

      const hasSelectedFile = !!(
        selectedFiles.loi ||
        selectedFiles.loa ||
        selectedFiles.fa
      );

      const hasUploadedInSession = !!(
        uploadedDocumentIds.loi ||
        uploadedDocumentIds.loa ||
        uploadedDocumentIds.fa
      );

      // Only show toast if NO documents exist at all
      if (
        !hasSelectedFile &&
        !hasUploadedInSession &&
        !hasAnyUploadedDoc &&
        !hasJustUploaded
      ) {
        toast.info("Please upload at least one document before submitting.");
        return;
      }

      setConfirmationModalOpen(false);
      setApprovalLoading(true);

      try {
        await acceptRejectAgreement(connectionId, 1);
        handleClose();
        setSuccessPopupOpen(true);

        if (typeof onAllDocsUploaded === "function") {
          onAllDocsUploaded();
        }

        if (listingId) {
          dispatch(fetchMatchmakingResults({ id: listingId }));
        }
        dispatch(fetchAllBankingListings());

        setTimeout(() => {
          window.location.reload();
        }, 4000);
      } catch (error) {
        toast.error(
          error?.response?.data?.message || "Failed to approve agreement.",
        );
      } finally {
        setApprovalLoading(false);
      }
    }
  };
  const handleReject = async () => {
    if (!connectionId) {
      toast.error("Connection ID is missing.");
      return;
    }

    setRejectionModalOpen(false);
    setConformReject(false);
    setApprovalLoading(true);

    try {
      if (mode === "capacity") {
        const res = await rejectAgreement(connectionId);
        if (res?.data?.code === 200) {
          dispatch(fetchActiveConnection({ curr: 0, capId: listingId }));
          dispatch(fetchMainScreenActiveConnection({ capId: listingId }));
        }
        handleClose();
        if (typeof onAllDocsUploaded === "function") {
          onAllDocsUploaded();
        }
      } else {
        await acceptRejectAgreement(connectionId, 0);

        // Reset upload tracking states after successful rejection
        setHasJustUploaded(false);
        setHasNewLoiOrLoaUpload(false);
        setSelectedFiles({
          loi: null,
          loa: null,
          fa: null,
          other: null,
        });
        setUploadedDocumentIds({
          loi: null,
          loa: null,
          fa: null,
          other: null,
        });

        handleClose();
        if (typeof onAllDocsUploaded === "function") {
          onAllDocsUploaded();
        }
        if (listingId) {
          dispatch(fetchMatchmakingResults({ id: listingId }));
        }
        dispatch(fetchAllBankingListings());
        setTimeout(() => {
          window.location.reload();
        }, 4000);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to reject agreement.",
      );
    } finally {
      setApprovalLoading(false);
    }
  };

  // Submit handler
  const handleSubmit = async () => {
    if (uploading) return;

    if (!connectionId) {
      toast.error("Connection ID is missing.");
      return;
    }

    if (mode === "capacity") {
      if (!hasCurrentLoiOrLoa) {
        toast.info("Please upload atleast one document before submitting.");
        return;
      }

      try {
        const res = await submitDocuments(connectionId);
        if (res?.data?.code === 200) {
          dispatch(apiConfig.fetchDocumentHistory(connectionId));
          dispatch(fetchActiveLedgerCapacity({ capId: listingId }));
          dispatch(fetchActiveConnection({ curr: 0, capId: listingId }));
          dispatch(fetchMainScreenActiveConnection({ capId: listingId }));
          handleClose();
        }
      } catch (err) {
        toast.error(
          err?.response?.data?.message ||
            "Failed to sync Final Agreement. Please try again.",
        );
      }
    } else {
      // Banking mode - check documents based on current user role
      const getCurrentUserParty = () => {
        if (!activeCounterparty) return null;
        const currentUserId = upData?.[0]?.selfListing?.userId;
        if (currentUserId === activeCounterparty.connectionInitiatorId) {
          return "INITIATOR";
        } else if (currentUserId === activeCounterparty.connectionAcceptorId) {
          return "ACCEPTOR";
        }
        return null;
      };

      const currentUserParty = getCurrentUserParty();
      let currentPartyDocIds;

      if (currentUserParty === "INITIATOR") {
        currentPartyDocIds = {
          loiDocId: activeCounterparty?.partyOneLoiDocumentId,
          loaDocId: activeCounterparty?.partyOneLoaDocumentId,
          agreementDocId: activeCounterparty?.partyOneAgreementDocumentId,
        };
      } else if (currentUserParty === "ACCEPTOR") {
        currentPartyDocIds = {
          loiDocId: activeCounterparty?.partyTwoLoiDocumentId,
          loaDocId: activeCounterparty?.partyTwoLoaDocumentId,
          agreementDocId: activeCounterparty?.partyTwoAgreementDocumentId,
        };
      }

      const hasAnyUploadedDoc = !!(
        currentPartyDocIds?.loiDocId ||
        currentPartyDocIds?.loaDocId ||
        currentPartyDocIds?.agreementDocId
      );

      const hasSelectedFile = !!(
        selectedFiles.loi ||
        selectedFiles.loa ||
        selectedFiles.fa
      );

      const hasUploadedInSession = !!(
        uploadedDocumentIds.loi ||
        uploadedDocumentIds.loa ||
        uploadedDocumentIds.fa
      );

      // Allow submission if any of these conditions are true
      const canProceed =
        hasSelectedFile ||
        hasUploadedInSession ||
        hasAnyUploadedDoc ||
        hasJustUploaded;

      if (!canProceed) {
        toast.info("Please upload at least one document before submitting.");
        return;
      }

      let fileToSubmit = selectedFiles.loi || selectedFiles.loa;
      let usedHistoryFile = false;

      if (!fileToSubmit) {
        const latestUserFile = await fetchLatestUserDocumentFile();

        if (latestUserFile) {
          fileToSubmit = latestUserFile;
          usedHistoryFile = true;
        } else {
          // Only show error if we truly have no documents
          if (!hasAnyUploadedDoc && !hasUploadedInSession && !hasJustUploaded) {
            toast.info(
              "Please upload at least one document before submitting.",
            );
            return;
          }
          // If we have uploaded documents but couldn't fetch file, proceed anyway
          // The backend will handle the existing documents
        }
      }

      try {
        if (fileToSubmit) {
          const formData =
            latestFormData instanceof FormData && !usedHistoryFile
              ? latestFormData
              : new FormData();
          formData.set("file", fileToSubmit);

          const response = await apiConfig.uploadFa(
            formData,
            connectionId,
            listingId,
          );

          if (response?.data?.id) {
            updateUploadedDocId("fa", response.data.id);
            setHasJustUploaded(true);
            toast.success("Final Agreement uploaded successfully!");
          }
        } else {
          // If no file to submit but we have uploaded documents, just show success
          toast.success("Proceeding with existing documents!");
        }

        await dispatch(apiConfig.fetchDocumentHistory(connectionId));

        if (typeof onRefresh === "function") onRefresh();

        if (typeof onAllDocsUploaded === "function") onAllDocsUploaded();

        handleClose();
      } catch (error) {
        const fallbackMessage =
          error?.response?.data?.message ||
          "Submission failed. Please try again.";
        toast.error(fallbackMessage);
      }
    }
  };

  const getLatestUserDocument = useCallback(() => {
    if (!Array.isArray(documentViewHistory) || documentViewHistory.length === 0)
      return null;

    const partyDocIds = isCounterparty
      ? [
          activeCounterparty?.partyOneLoiDocumentId,
          activeCounterparty?.partyOneLoaDocumentId,
          activeCounterparty?.partyOneAgreementDocumentId,
        ]
      : [
          activeCounterparty?.partyTwoLoiDocumentId,
          activeCounterparty?.partyTwoLoaDocumentId,
          activeCounterparty?.partyTwoAgreementDocumentId,
        ];

    const validIds = partyDocIds.filter(Boolean);
    const userDocs =
      validIds.length > 0
        ? documentViewHistory.filter((doc) => validIds.includes(doc.id))
        : documentViewHistory;

    return userDocs
      .slice()
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
  }, [documentViewHistory, isCounterparty, activeCounterparty]);

  const fetchLatestUserDocumentFile = useCallback(async () => {
    const latestDoc = getLatestUserDocument();
    if (!latestDoc) return null;

    try {
      const urlResponse = await getDocumentUrlByLedgerId(latestDoc.id);
      const fileUrl = urlResponse?.data;
      if (!fileUrl) return null;

      const fileResponse = await fetch(fileUrl);
      const blob = await fileResponse.blob();
      const filename = latestDoc.filename || "document.pdf";
      const type = blob.type || "application/pdf";
      return new File([blob], filename, { type });
    } catch (err) {
      return null;
    }
  }, [getLatestUserDocument]);

  const handleCommentsChange = (text) => {
    const words = text.trim().split(/\s+/);
    if (words.length <= 50) {
      setComments(text);
    } else {
      const limitedText = words.slice(0, 50).join(" ");
      setComments(limitedText);
    }
  };

  // Computed checks
  const hasLoiOrLoaInConnection = useMemo(() => {
    if (mode === "capacity") {
      if (!activeConnection) return false;
      if (role === "generator") {
        return !!(activeConnection.genLoiDocId || activeConnection.genLoaDocId);
      }
      return !!(activeConnection.conLoiDocId || activeConnection.conLoaDocId);
    }
    return false;
  }, [mode, activeConnection, role]);

  const hasCurrentLoiOrLoa =
    hasNewLoiOrLoaUpload ||
    selectedFiles.loi ||
    selectedFiles.loa ||
    hasLoiOrLoaInConnection;

  const hasUploadedAnyDocument = useMemo(() => {
    if (mode === "capacity") {
      let hasLoiOrLoaOrAgreementInConnection = false;
      if (activeConnection) {
        if (role === "generator") {
          hasLoiOrLoaOrAgreementInConnection = !!(
            activeConnection.genLoiDocId ||
            activeConnection.genLoaDocId ||
            activeConnection.genAgreementDocId
          );
        } else {
          hasLoiOrLoaOrAgreementInConnection = !!(
            activeConnection.conLoiDocId ||
            activeConnection.conLoaDocId ||
            activeConnection.conAgreementDocId
          );
        }
      }
      return (
        selectedFiles.loi !== null ||
        selectedFiles.loa !== null ||
        selectedFiles.fa !== null ||
        selectedFiles.other !== null ||
        hasNewLoiOrLoaUpload ||
        hasLoiOrLoaOrAgreementInConnection
      );
    } else {
      // Banking mode - determine current user's party
      const getCurrentUserParty = () => {
        if (!activeCounterparty) return null;
        const currentUserId = upData?.[0]?.selfListing?.userId;
        if (currentUserId === activeCounterparty.connectionInitiatorId) {
          return "INITIATOR";
        } else if (currentUserId === activeCounterparty.connectionAcceptorId) {
          return "ACCEPTOR";
        }
        return null;
      };

      const currentUserParty = getCurrentUserParty();
      let currentPartyDocIds;

      if (currentUserParty === "INITIATOR") {
        currentPartyDocIds = {
          loiDocId: activeCounterparty?.partyOneLoiDocumentId,
          loaDocId: activeCounterparty?.partyOneLoaDocumentId,
          agreementDocId: activeCounterparty?.partyOneAgreementDocumentId,
        };
      } else if (currentUserParty === "ACCEPTOR") {
        currentPartyDocIds = {
          loiDocId: activeCounterparty?.partyTwoLoiDocumentId,
          loaDocId: activeCounterparty?.partyTwoLoaDocumentId,
          agreementDocId: activeCounterparty?.partyTwoAgreementDocumentId,
        };
      }

      // Check if current party has any uploaded documents in the backend
      const hasAnyUploadedDoc = !!(
        currentPartyDocIds?.loiDocId ||
        currentPartyDocIds?.loaDocId ||
        currentPartyDocIds?.agreementDocId
      );

      // Check if user has selected files in current session
      const hasSelectedFiles =
        selectedFiles.loi !== null ||
        selectedFiles.loa !== null ||
        selectedFiles.fa !== null;

      // Check if user has uploaded in current session
      const hasUploadedInSession =
        uploadedDocumentIds.loi !== null ||
        uploadedDocumentIds.loa !== null ||
        uploadedDocumentIds.fa !== null;

      // Return true if ANY of these conditions are met
      return (
        hasSelectedFiles ||
        hasUploadedInSession ||
        hasJustUploaded ||
        hasAnyUploadedDoc
      );
    }
  }, [
    mode,
    selectedFiles,
    uploadedDocumentIds,
    hasNewLoiOrLoaUpload,
    hasJustUploaded,
    activeConnection,
    activeCounterparty,
    upData,
    role,
  ]);

  const filteredDocuments = useMemo(() => {
    if (!documentViewHistory || !Array.isArray(documentViewHistory)) return [];

    if (mode === "banking") {
      return [...documentViewHistory].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
      );
    }
    return [...documentViewHistory].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
    );
  }, [documentViewHistory, mode]);

  const handleClosePreview = () => {
    setPreviewModal(false);
    setPreviewFileUrl(null);
  };

  const resetAllStates = () => {
    setComments("");
    setHasNewLoiOrLoaUpload(false);
  };

  useEffect(() => {
    if (connectionId && open) {
      dispatch(apiConfig.fetchDocumentHistory(connectionId));
    }
  }, [dispatch, connectionId, open, apiConfig]);

  useEffect(() => {
    if (open) {
      setDataFetched(false);
    }
  }, [open]);

  const UploadSection = ({
    title,
    generateHandler,
    showGenerate = true,
    fileInputId,
    fileName,
    placeholder,
    onView,
    onRemove,
    disabled,
    required = false,
    width,
  }) => {
    return (
      <div className={Styles.uploadFile}>
        <div className={`display_flex_row_center ${Styles.upperHead}`}>
          <span className={Styles.headerName}>{title}</span>
          {showGenerate && (
            <span
              className={Styles.generateHead}
              onClick={disabled ? undefined : generateHandler}
              style={{
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled ? 0.6 : 1,
              }}
            >
              Generate
            </span>
          )}
        </div>
        <div>
          <label
            htmlFor={fileName ? undefined : fileInputId}
            className={Styles.uploadLabelHeader}
            style={{
              pointerEvents: disabled ? "none" : "auto",
              opacity: disabled ? 0.6 : 1,
            }}
            onClick={(e) => {
              if (disabled) return;
              if (fileName) {
                e.preventDefault();
                onView();
              }
            }}
          >
            <span className="display_flex_row_center">
              {fileName ? <ViewUploadFile /> : <UploadIcon />}
            </span>
            <span className={`display_flex_row_center ${Styles.fileNameText}`}>
              {fileName || placeholder}
            </span>
            {fileName && (
              <span
                className="display_flex_row_center"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onRemove();
                }}
                style={{ cursor: "pointer" }}
              >
                <CrossIcon />
              </span>
            )}
          </label>
          <input
            type="file"
            id={fileInputId}
            name={fileInputId}
            accept=".pdf"
            style={{ display: "none" }}
            onChange={handleFileChange}
            disabled={disabled}
          />
        </div>
      </div>
    );
  };

  return (
    <>
      {mode === "capacity" && (
        <Ongoing
          open={ongoing}
          onClose={() => {
            setOngoing(false);
            handleClose();
            dispatch(fetchActiveConnection({ curr: 0, capId: listingId }));
          }}
        />
      )}

      {mode === "banking" && (
        <SuccessPop
          open={successPopupOpen}
          handleClose={() => setSuccessPopupOpen(false)}
        />
      )}

      <Modal open={open} onClose={handleClose}>
        <div className={`popupModal ${Styles.createEditModalCont}`}>
          <div
            className={`display_flex_row modalHeading ${Styles.headingText}`}
          >
            <div className="headingText">Generate and Upload Docs</div>
            <Close
              style={{ fontSize: "24px", color: "#777777", cursor: "pointer" }}
              onClick={() => {
                resetAllStates();
                handleClose();
              }}
            />
          </div>

          <div
            className={`display_flex_row_center ${Styles.uploadDocMainContainer}`}
          >
            {/* Upload Files Section */}
            <div className={`display_flex_row ${Styles.uploadFilesSection}`}>
              <UploadSection
                title="Letter of Intent"
                generateHandler={documentGenerators.loi}
                fileInputId="fileUpload"
                fileName={selectedFiles.loi?.name || ""}
                placeholder="Upload Letter of Intent.pdf"
                onView={() => {
                  if (selectedFiles.loi) {
                    const url = URL.createObjectURL(selectedFiles.loi);
                    setPreviewFileUrl(url);
                    setPreviewModal(true);
                  }
                }}
                onRemove={() => resetFileSelection("loi")}
                // disabled={documentStates.isLoiDisabled}
              />

              <UploadSection
                title="Letter of Award"
                generateHandler={documentGenerators.loa}
                fileInputId="fileUploadLoa"
                fileName={selectedFiles.loa?.name || ""}
                placeholder="Upload Letter of Award.pdf"
                onView={() => {
                  if (selectedFiles.loa) {
                    const url = URL.createObjectURL(selectedFiles.loa);
                    setPreviewFileUrl(url);
                    setPreviewModal(true);
                  }
                }}
                onRemove={() => resetFileSelection("loa")}
                // disabled={documentStates.isLoaDisabled}
              />

              <UploadSection
                title="Final Agreement"
                showGenerate={true}
                generateHandler={documentGenerators.fa}
                fileInputId="fileUploadFa"
                fileName={selectedFiles.fa?.name || ""}
                placeholder="Upload Final Agreement.pdf"
                onView={() => {
                  if (selectedFiles.fa) {
                    const url = URL.createObjectURL(selectedFiles.fa);
                    setPreviewFileUrl(url);
                    setPreviewModal(true);
                  }
                }}
                onRemove={() => resetFileSelection("fa")}
                // disabled={documentStates.isFinalAgreementDisabled}
                required={false}
              />

              <UploadSection
                title="Other Documents"
                generateHandler={() => {}}
                fileInputId="fileUploadOther"
                fileName={selectedFiles.other?.name || ""}
                placeholder="Upload Other Document.pdf"
                onView={() => {
                  if (selectedFiles.other) {
                    const url = URL.createObjectURL(selectedFiles.other);
                    setPreviewFileUrl(url);
                    setPreviewModal(true);
                  }
                }}
                onRemove={() => resetFileSelection("other")}
                showGenerate={false}
                // disabled={documentStates.isOtherDocumentsDisabled}
              />
            </div>

            <div className={Styles.uploadFilesHistorySection}>
              <div className={Styles.upHistoryHeader}>Upload History</div>
              {loading ? (
                <div>Loading...</div>
              ) : error ? (
                <div style={{ color: "red" }}>Failed to load history.</div>
              ) : Array.isArray(documentViewHistory) &&
                documentViewHistory?.length > 0 ? (
                <div className={`scroll_bar_y ${Styles.uploadContentSection}`}>
                  {documentViewHistory?.map((doc) => {
                    const datetime = doc.createdAt || "";
                    // const [datePart, timeWithMs] = datetime.split("T") || [
                    //   "",
                    //   "",
                    // ];
                    const dateObj = doc.createdAt
                      ? new Date(doc.createdAt)
                      : null;
                    const datePart = dateObj
                      ? dateObj.toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "short",
                          year: "2-digit",
                        })
                      : // .toUpperCase()
                        "";
                    //   ? timeWithMs.split(".")[0]
                    //   : "";
                    const timePart = dateObj
                      ? dateObj.toLocaleTimeString("en-GB", {
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: false,
                        })
                      : "";

                    const getDocumentTypeName = (docType) => {
                      switch (docType?.toUpperCase()) {
                        case "LOI":
                          return "LOI - ";
                        case "LOA":
                          return "LOA - ";
                        case "AGREEMENT":
                        case "FA":
                          return "Final Agreement - ";
                        case "RANDOM":
                        case "OTHER":
                          return "Other Document - ";
                        default:
                          return "";
                      }
                    };

                    return (
                      <div
                        key={doc.id}
                        className={Styles.upHistoryContent}
                        // style={{ flexShrink: 0 }}
                      >
                        <div className={Styles.docFileCont}>
                          {(() => {
                            const entityName =
                              mode === "capacity"
                                ? doc.entityName || "-"
                                : doc.companyName || "Untitled Document";
                            const fileName =
                              doc.filename || "Untitled Document";
                            const commentText = doc.comment || doc.comments || "";
                            return (
                              <>
                                <span
                                  className={Styles.docFileMeta}
                                  title={entityName}
                                  style={{
                                    fontSize: "10px",
                                    color: "var(--color-text-inactive)",
                                  }}
                                >
                                  {entityName}
                                </span>
                                <span
                                  className={Styles.docFileName}
                                  title={fileName}
                                >
                                  <strong>
                                    {getDocumentTypeName(doc.documentType)}
                                  </strong>
                                  {fileName}
                                </span>
                                <span
                                  className={Styles.docFileMeta}
                                  title={commentText}
                                  style={{
                                    color: "var(--color-text-inactive)",
                                  }}
                                >
                                  {commentText}
                                </span>
                              </>
                            );
                          })()}
                        </div>

                        <div className={Styles.docFileDT}>
                          <div>
                            {/* {mode === "capacity"
                              ? formatDate(datePart)
                              : datePart} */}
                            {datePart}
                          </div>
                          <div>{timePart}</div>
                        </div>

                        <div className={Styles.docFileView}>
                          <div
                            onClick={() => handleViewDoc(doc.id)}
                            style={{ cursor: "pointer" }}
                            className={Styles.viewIcon}
                          >
                            <ViewIcon />
                          </div>
                          {mode === "banking" && (
                            <div
                              onClick={() =>
                                handleDownloadDocument(doc.id, doc.filename)
                              }
                              style={{ cursor: "pointer", marginLeft: "8px" }}
                            >
                              <DownloadIcon />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className={Styles.noDocData}>
                  <div>No documents uploaded yet.</div>
                </div>
              )}
            </div>
          </div>

          {/* Buttons Section */}
          {showApprovalButtons ? (
            <div className={Styles.btnSection}>
              <div
                type="button"
                onClick={() => {
                  if (mode === "capacity") {
                    setConformReject(true);
                  } else {
                    setRejectionModalOpen(true);
                  }
                }}
                disabled={approvalLoading}
                style={{
                  padding: "8px 16px",
                  cursor: approvalLoading ? "not-allowed" : "pointer",
                }}
                className={Styles.rejectButton}
              >
                Reject
              </div>
              <div
                type="button"
                className={Styles.buttonSubmit}
                onClick={() => {
                  if (mode === "capacity") {
                    setConformOpen(true);
                  } else {
                    setConfirmationModalOpen(true);
                  }
                }}
                disabled={approvalLoading}
                style={{
                  padding: "8px 16px",
                  cursor: approvalLoading ? "not-allowed" : "pointer",
                }}
              >
                Proceed
              </div>
            </div>
          ) : (
            <div className={Styles.btnSection}>
              <div
                type="button"
                className={Styles.buttonSubmit}
                onClick={() => setSubmitConfirmOpen(true)}
                style={{
                  padding: "8px 16px",
                  opacity: uploading ? 0.6 : 1,
                  pointerEvents: uploading ? "none" : "auto",
                }}
              >
                Submit
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Modals */}
      <ConfirmationModal
        open={submitConfirmOpen}
        loading={uploading}
        handleClose={() => setSubmitConfirmOpen(false)}
        handleSubmit={async () => {
          setSubmitConfirmOpen(false);
          await handleSubmit();
        }}
        header="Submit Documents"
        data={
          hasUploadedAnyDocument
            ? "Your document will be submitted to the counterparty as the Final Agreement for their approval and execution. Do you want to proceed?"
            : "You have not uploaded any document. Your submission will proceed to the counterparty as the Final Agreement for their approval and execution. Do you want to proceed?"
        }
      />

      <ConfirmationModal
        open={uploadConfirmOpen}
        loading={uploading}
        commentBox={pendingUploadData?.displayName === "Other Documents"}
        commentBoxHeading="Comments"
        handleCommentChange={handleCommentsChange}
        customStyle={{ minHeight: "160px" }}
        handleClose={handleCancelUpload}
        handleSubmit={performUpload}
        header={pendingUploadData?.displayName || "Document"}
        data={
          showApprovalButtons
            ? "Do you want to upload and share this document with the counterparty?"
            : "Do you want to submit the final agreement for approval by the counterparty?"
        }
        note={
          showApprovalButtons &&
          "If you've made any changes to the final document, please share it with the other party via chat for approval."
        }
      />

      <ConfirmationModal
        open={conformOpen}
        loading={approvalLoading}
        handleClose={() => setConformOpen(false)}
        handleSubmit={handleApprove}
        header="Upload Docs Confirmation"
        data={
          hasUploadedAnyDocument
            ? "Once approved, this listing will move to the ledger, and the connection will be finalized. \n\nPlease ensure that you have uploaded the countersigned document."
            : "You have not uploaded any document. Once approved, this listing will move to the ledger, and the connection will be finalized."
        }
        note={
          showApprovalButtons &&
          "If you've made any changes to the final document, please share it with the other party via chat for approval."
        }
      />

      <ConfirmationModal
        open={conformReject}
        loading={approvalLoading}
        handleClose={() => setConformReject(false)}
        handleSubmit={handleReject}
        header="Reject Agreement"
        data="Do you want to reject this agreement?"
        note="After rejecting the agreement, you may either wait for the counterparty to upload a revised version or upload a new version yourself."
      />

      <ConfirmationModal
        open={confirmationModalOpen}
        handleClose={() => setConfirmationModalOpen(false)}
        header="Approve Agreement"
        data={
          hasUploadedAnyDocument
            ? "Are you sure you want to approve the document(s)? Once approved, this listing will move to the ledger, and the connection will be finalized.\nPlease ensure that you have uploaded the countersigned document."
            : "You have not uploaded any document.Please upload at least one document to proceed."
        }
        handleSubmit={handleApprove}
        loading={approvalLoading}
        customStyle={{ height: "240px" }}
        note={
          showApprovalButtons &&
          "If you've made any changes to the final document, please share it with the other party via chat for approval."
        }
      />

      <ConfirmationModal
        open={rejectionModalOpen}
        handleClose={() => setRejectionModalOpen(false)}
        header="Reject Agreement"
        data="Do you want to reject this agreement?"
        note="After rejecting the agreement, you may either wait for the counterparty to upload a revised version or upload a new version yourself."
        handleSubmit={handleReject}
        loading={approvalLoading}
        customStyle={{ height: "200px" }}
      />

      <PreviewModal
        open={previewModal}
        onClose={handleClosePreview}
        fileUrl={previewFileUrl}
      />
    </>
  );
};

export default DirectDocumentation;
